import type { SearchFilterInstance } from "../../types/searchFilterInstance";
import type { IPinService } from "../pin-service/types";

import type {
    CollapsedVisibilityContext,
    FilterVisibilityParams,
    FilterVisibilityState,
    IFiltersService,
} from "./types";

export const createFiltersService = <TFilter extends SearchFilterInstance = SearchFilterInstance>(
    pinService: IPinService,
): IFiltersService<TFilter> => {
    const DEFAULT_FILTERS_LIST: readonly TFilter[] = [];

    const withPermanentFirst = (filters: readonly TFilter[]): readonly TFilter[] => {
        const permanent = filters.filter((f) => f.visibility === "permanent");
        const rest = filters.filter((f) => f.visibility !== "permanent");

        return [...permanent, ...rest];
    };

    const isFilterHidden = (filter: TFilter, ctx: CollapsedVisibilityContext): boolean => {
        if (filter.visibility === "permanent" || filter.visibility === "always-visible") {
            return false;
        }

        // A filter holding a value is never hidden, whoever set it — the panel pins its own edits, but
        // values also arrive from outside it (territory overlay, saved search, AI agent).
        if (ctx.dirtyFilterKeys.has(filter.key)) {
            return false;
        }

        if (pinService.instanceMatchesPin(filter, ctx.selectedFilterKeys)) {
            return false;
        }

        return true;
    };

    const getVisibleFilters = (
        params: FilterVisibilityParams<TFilter>,
        state: FilterVisibilityState,
    ): readonly TFilter[] => {
        const { filtersConfig, selectedFilterKeys, dirtyFilterKeys, isSearchActive, searchMatchedFilters } = params;
        const { isCategoryPanelExpanded } = state;

        // Priority 1: Search results take precedence
        if (isSearchActive) {
            return searchMatchedFilters ?? DEFAULT_FILTERS_LIST;
        }

        // Priority 2: Categories panel expanded — show all filters
        if (isCategoryPanelExpanded) {
            return withPermanentFirst(filtersConfig);
        }

        // Priority 3: Collapsed view
        const ctx: CollapsedVisibilityContext = { selectedFilterKeys, dirtyFilterKeys };

        return withPermanentFirst(filtersConfig.filter((item) => !isFilterHidden(item, ctx)));
    };

    return {
        getVisibleFilters,
        isFilterHidden,
    };
};
